import * as React from 'react';
import axios from 'axios';
import AxiosConfig from '../../utilities/AxiosConfig';
import { PTOEntryType, PTOPlanType } from './types';
import CollapsibleArea from '../../components/common/CollapsibleArea';
import PTOAdminTableWrapper from './PTOAdminTableWrapper';
import { Spinner } from 'react-bootstrap';
import './PTOStateCollapse.css';

interface IProps {
    state: { name: string, id: any } | null;
    fromToDate: { from: string, to: string };
    includeInactive: boolean;
    reference: { states: Array<any>, profitCenters: Array<any>, departments: Array<any>, ptoPlans: Array<PTOPlanType> };
    profitCenters: Array<any>;
    searchValue: string;
    showOnlyPending: boolean;
    displayZeros: boolean;
    setDisplayZeros: (displayZeros: boolean) => void;
    expandDetail: boolean;
    setExpandDetail: (expandDetail: boolean) => void;
    expandState: boolean;
    expandCity: boolean;
    setExpandCity: (expandCity: boolean) => void;
}

const PTOStateCollapse: React.FC<IProps> = (props: IProps) => {
    const { state, reference, fromToDate, includeInactive, profitCenters, searchValue, showOnlyPending, displayZeros, setDisplayZeros, expandDetail, setExpandDetail, expandState, expandCity, setExpandCity } = props;
    interface Totals {
        balance: number;
        pending: number;
        used: number;
    }
    const originalProfitCentersData = React.useRef({} as any);
    const [profitCentersPTOData, setProfitCentersPTOData] = React.useState({} as {
        [key: string]: {
            data: Array<PTOEntryType>;
            ledgerEntryTypes: Array<any>;
            ledgerStatusTypes: Array<any>;
        }
    });
    const [total, setTotal] = React.useState({
        balance: 0,
        pending: 0,
        used: 0
    } as Totals);
    const [loading, setLoading] = React.useState(true);

    const getStateTotal = () => {
        setLoading(true);
        let requestsOrder: Array<string> = [];
        let requests: Array<any> = [];
        profitCenters.forEach((pc: any) => {
            requestsOrder.push(pc?.value);
            requests.push(axios.get(process.env.REACT_APP_API_URL + `/api/pto/ptodata/${pc?.value}/${fromToDate.from.valueOf()}/${fromToDate.to.valueOf()}/${includeInactive}`, AxiosConfig.configValue("min")))
        })
        axios.all(requests)
            .then((responses: any) => {
                if (responses.length > 0) {
                    //r-18-lint-id
                    // eslint-disable-next-line
                    let isError: boolean = false;
                    let profitCenterData: any = {};
                    responses.forEach((res: any, resIndex: number) => {
                        if (AxiosConfig.checkResponse(res, { alertOnNull: false })) {
                            profitCenterData[requestsOrder[resIndex]] = res.data;
                        } else {
                            isError = true;
                        }
                    });
                    setProfitCentersPTOData(profitCenterData);
                    originalProfitCentersData.current = profitCenterData;
                }
                setLoading(false);
            })
            .catch((err: any) => {
                //console.log("Error: ", err);
                setLoading(false);
                AxiosConfig.handleError(err);
            })
            .finally(() => {
                // setLoading(false);
            })
    }
    //TODO: Updating a single item
    const updateSingleProfitCenter = (profitCenterId: number | string) => {
        const pcid: number = Number(profitCenterId);
        if (profitCentersPTOData[pcid]) {
            axios.get(process.env.REACT_APP_API_URL + `/api/pto/ptodata/${pcid}/${fromToDate.from.valueOf()}/${fromToDate.to.valueOf()}/${includeInactive}`, AxiosConfig.configValue("min"))
                .then((res: any) => {
                    let copy: any = { ...profitCentersPTOData };
                    copy[pcid] = res.data;
                    setProfitCentersPTOData(copy);
                })
                .catch((err: any) => {
                    AxiosConfig.handleError(err);
                })
        }
    }

    const updateTotals = () => {
        let newTotals: Totals = { balance: 0, pending: 0, used: 0 } as Totals;
        let data: Array<any> = [];
        Object.keys(profitCentersPTOData).forEach((pcid: any) => {
            data = [...data, ...profitCentersPTOData[pcid].data];
        });
        data = data.filter((entry: PTOEntryType) => {
            if (showOnlyPending && entry.PendingHours === 0) return false;
            return entry.DisplayName.toLowerCase().includes(props.searchValue.toLowerCase())
        });
        data.forEach((entry: PTOEntryType) => {
            newTotals["balance"] += Math.round(entry.CurrentBalance) || 0;
            newTotals["pending"] += Math.abs(Math.round(entry.PendingHours)) || 0;
            newTotals["used"] += Math.abs(Math.round(entry.UsedYTD)) || 0;
        });
        setTotal(newTotals);
    }

    React.useEffect(() => {
        if (reference && fromToDate) {
            getStateTotal();
            //r-18-lint-dj
        }// eslint-disable-next-line
    }, [fromToDate, includeInactive])

    React.useEffect(() => {
        updateTotals();
        //r-18-lint-dj
        // eslint-disable-next-line
    }, [profitCentersPTOData, searchValue, showOnlyPending])


    return (
        <CollapsibleArea
            Toggle={(({ onClick, open }: any) => (
                // conditional rendering the bar color and background based on whether it's selected and or has requested hours pending
                <div id={`state-cell-${state?.id}`} className={
                    total.pending > 0 && open ?
                        `state-cell warning state-selected d-flex flex-grow-1 p-1 action-text align-items-center` :
                        total.pending > 0 && !open ?
                            `state-cell warning d-flex flex-grow-1 p-1 action-text align-items-center` :
                            total.pending <= 0 && open ?
                                `state-cell state-selected d-flex flex-grow-1 p-1 action-text align-items-center` :
                                `state-cell d-flex flex-grow-1 p-1 action-text align-items-center`
                }
                    onClick={onClick}>
                    <div className={total.pending === 0 && !displayZeros ? "grayed 'state-label' totals-divs requested-div d-inline-flex flex-row align-items-center" : "'state-label' totals-divs requested-div d-inline-flex flex-row align-items-center"}>
                        <h5>{state?.name || "NO STATE!"}</h5>
                    </div>
                    <div className='flex-shrink-1'>
                        <div className={total.balance === 0 && !displayZeros ? "grayed totals-divs d-inline-flex flex-row align-items-center" : "totals-divs d-inline-flex flex-row align-items-center"}>
                            <strong>Balance:&nbsp; </strong>
                            <strong>
                                {loading ? <Spinner size="sm" animation="border" /> : <span className={total.balance === 0 && !displayZeros ? "grayed mx-auto" : "mx-auto"}>{total.balance}</span>}
                            </strong>
                        </div>
                        <div className={total.used === 0 && !displayZeros ? "grayed totals-divs d-inline-flex flex-row align-items-center" : "totals-divs d-inline-flex flex-row align-items-center"}>
                            <strong>Used: &nbsp;</strong>
                            <strong>
                                {loading ? <Spinner size="sm" animation="border" /> : <span className={total.used === 0 && !displayZeros ? "grayed mx-auto" : "mx-auto"}>{total.used}</span>}
                            </strong>
                        </div>
                        {/* <div className={(total.balance + total.used)  === 0 && !displayZeros ? "grayed totals-divs d-inline-flex flex-row align-items-center" : "totals-divs d-inline-flex flex-row align-items-center"}>
                            <strong>Total: &nbsp;</strong>
                            <strong>
                            {loading ? <Spinner size="sm" animation="border" /> : <span className={(total.balance + total.used)  === 0 && !displayZeros ? "grayed mx-auto" : "mx-auto"}>{total.used + total.balance}</span>}
                            </strong>
                        </div> */}
                        <div className={total.pending === 0 && !displayZeros ? "grayed totals-divs requested-div d-inline-flex flex-row align-items-center" : "totals-divs requested-div d-inline-flex flex-row align-items-center"}>
                            <strong className='requested-text'>Requested: </strong>
                            {loading ? <Spinner size="sm" animation="border" /> : <span className={total.pending > 0 ? "state-requested-number" : "state-requested-number-zero "}>{total.pending}</span>}
                        </div>
                    </div>
                    <i className="material-icons mr-1 ml-auto">{open ? "expand_less" : "expand_more"}</i>
                </div>
            ))}
            startOpen={expandState}
        >
            <hr className="p-0 m-0 w-100 ml-3" style={{ borderTop: "2px solid lightgrey" }} />
            <div className="pl-3">

                {
                    profitCentersPTOData
                    &&
                    Object.keys(profitCentersPTOData)
                        // .filter((pcid: any) => profitCenters.findIndex((pc: any) => pc.value === Number(pcid)) > -1)
                        .map((pcid: any) => {
                            let pcReference: any = reference.profitCenters.find((pc: any) => pc.id === Number(pcid));
                            let ptoData = profitCentersPTOData[pcid];
                            // ptoData.data = ptoData.data.filter((entry: PTOEntryType) => entry.DisplayName.toLowerCase().includes(props.searchValue.toLowerCase()))
                            return <PTOAdminTableWrapper
                                key={pcid}
                                currentProfitCenter={pcReference}
                                fromToDate={fromToDate}
                                includeInactive={includeInactive}
                                reference={reference}
                                ptoData={ptoData}
                                updateTable={updateSingleProfitCenter}
                                searchValue={searchValue}
                                showOnlyPending={showOnlyPending}
                                displayZeros={displayZeros}
                                setDisplayZeros={setDisplayZeros}
                                expandDetail={expandDetail}
                                setExpandDetail={setExpandDetail}
                                expandCity={expandCity}
                                setExpandCity={setExpandCity}
                            />
                        })
                }
            </div>
        </CollapsibleArea>
    )
}

export default PTOStateCollapse;